import { getSwRevision } from 'serviceWorker/build';
import { getSwOmicronConfig } from 'serviceWorker/omicronConfig';
import type { SwOmicronConfig } from 'serviceWorker/omicronConfigBridge';
import { type SwPlatform, resolveSwPlatform } from 'serviceWorker/platform';
import type { AppOpenReason } from 'serviceWorker/push/appPresence';
import type { SkipReason } from 'serviceWorker/push/skipRules';
import { swXray } from 'serviceWorker/xray';

/** Первый сегмент имени служебных метрик воркера. */
const FEATURE = 'web-push-sw';

/** Проект продуктовых событий: под ним заведены селекторы в Метиде. */
const DWH_P = 'cloud-web-analytics';

/** Сегментами: Метида ожидает pwa_push, swXray.send склеивает через `_` поэтому нам подходит только сегментами */
const DWH_T = ['pwa', 'push'];

export type PushAction = 'delivered' | 'opened' | 'closed' | 'dedup' | 'suppressed';

/**
 * Почему пуш подавлен. Ступени проверки «приложение открыто» различаются между собой не ради
 * красоты: по ним видно, какая из них реально срабатывает на какой платформе, и нужен ли маяк
 * вообще. `dedup` сюда не входит — это отдельное действие со своим событием.
 */
export type SuppressReason = AppOpenReason | SkipReason;

/** Диплинковый пуш (`app_url`) против веблинкового (`web_url`). */
export type PushType = 'dl' | 'wl';

export interface PushEventData {
    /** Кампания мейлера. Пустая у пушей, отправленных вне кампании. */
    campaignId?: string;
    /** Куда ведёт пуш: `app_url` либо `web_url`. */
    url?: string;
    /**
     * Тип пуша. Не задан — тип **неизвестен**, а не «веблинк»: так бывает у secure-пуша,
     * подавленного до того, как тело дотянули из API, — url'ов в сыром payload ещё нет.
     * Пустое значение честнее выдуманного `wl`, потому что `type` уходит в разбивку Графаны.
     */
    type?: PushType;
    /** Secure-пуш: тело дотягивалось из API. */
    secure: boolean;
    /**
     * Идентификатор пуша. Уезжает в сырьё DWH и в debug-лог, но не в `dwh_enabled_fields`:
     * он уникален на каждый пуш, то есть дал бы в Graphite серию на каждый пуш.
     */
    id?: string;
    /**
     * Адресат из payload. Едет в `dwh`-пейлоад, то есть в продуктовую аналитику, где его читает
     * аналитик, — а не top-level параметром радара.
     *
     * В `dwh_enabled_fields` не входит и входить не должен: значение уникально на пользователя,
     * то есть дало бы в Graphite серию на каждого. Ровно та же причина, что у `id`.
     *
     * Значение `any` не подставляем — поле остаётся пустым: это честное «адресат в пуше не
     * указан», а не догадка про активный аккаунт.
     */
    email?: string;
    /** Причина подавления; есть только у действия `suppressed`. */
    reason?: SuppressReason;
}

const swRevision = getSwRevision();

/**
 * Ревизия попадает в имя метрики, а неподставленный плейсхолдер `8a967490b573` сделал бы его
 * невалидным. В дев-сборке подстановки нет — версионные метки просто не шлём.
 */
const hasRevision = !swRevision.includes('%');

/** Разрезы события пуша, которые считаются из снимка конфига. */
export interface PushDimensions {
    platform: SwPlatform;
    isPwa: boolean;
}

/**
 * Платформа и режим отображения — одним чтением снимка, пришедшего со страницы.
 *
 * Платформу берём из снимка через `resolveSwPlatform`, а не из UA воркера: на iPadOS 13+
 * Safari отдаёт маковский UA без "iPad", и по одному UA воркер отличить его от макбука не может —
 * `maxTouchPoints` есть только на `Navigator`, в `WorkerNavigator` его нет. Без снимка (первый визит,
 * страница старой версии) падаем на UA, то есть на прежнее поведение.
 *
 * Решающая логика подавления уже ходит этим же путём (`getEffectiveSwPlatform` в handlePush) —
 * здесь разметка событий приводится к тому же словарю, иначе «подавления по платформам» и
 * «доставки по платформам» на этом сегменте несопоставимы.
 */
export function resolvePushDimensions(config: SwOmicronConfig | null, ua: string): PushDimensions {
    const platform = resolveSwPlatform(config?.platform, ua);

    return {
        platform,
        // На iOS web-пуши существуют только внутри установленного PWA, поэтому там ios === pwa.
        isPwa: platform === 'ios' ? true : Boolean(config?.isStandalone),
    };
}

/**
 * Тип пуша по тому, куда он ведёт. `undefined` — ведёт неизвестно куда: у secure-пуша url'ы
 * лежат в теле, которое дотягивается из API, и до этого момента их в payload нет.
 *
 * Раньше тип считался как «есть `app_url` → dl, иначе wl», и подавленный до дотягивания
 * secure-пуш попадал в аналитику веблинковым независимо от того, чем был на самом деле.
 */
export function resolvePushType(appUrl: string, webUrl: string): PushType | undefined {
    if (appUrl) {
        return 'dl';
    }

    if (webUrl) {
        return 'wl';
    }

    return undefined;
}

/**
 * Адресат пуша для аналитики: берётся из payload и ниоткуда больше.
 *
 * `any` — не адрес: pushme ставит его, когда кампания не адресована конкретному ящику
 * (CLOUDWEB-12097). Подставлять вместо адресата активный аккаунт страницы нельзя — при
 * мультиавторизации это другой ящик, и в DWH такая подмена неотличима от верных данных.
 * Пустое значение честнее выдуманного.
 */
export function resolvePushEmail(raw: string): string | undefined {
    if (!raw || raw === 'any') {
        return undefined;
    }

    return raw;
}

export const trackPushEvent = async (
    action: PushAction,
    { campaignId, url, type, secure, id, email, reason }: PushEventData
): Promise<void> => {
    const { platform, isPwa } = resolvePushDimensions(await getSwOmicronConfig(), navigator.userAgent || '');

    swXray.send(
        DWH_T,
        { [action]: 1 },
        {
            p: DWH_P,
            dwh: {
                action,
                campaign_id: campaignId || '',
                url: url || '',
                is_pwa: isPwa,
                platform,
                type: type || '',
                secure,
                id: id || '',
                email: email || '',
                ...(reason ? { reason } : {}),
            },
            dwh_enabled_fields: [
                // Цепочки с `type` — только когда тип известен: иначе в Graphite завелись бы вечные
                // серии с пустым звеном. Такие события всё равно видны в цепочке причин ниже.
                ...(type ? [['action', 'type', 'platform', 'secure']] : []),
                ...(type && campaignId ? [['action', 'type', 'campaign_id']] : []),
                // Даёт в Graphite серии вида
                // `radar.cnt_min.cloud-web-analytics.<prefix>pwa_push.action.suppressed.reason.*.platform.*`.
                //
                // Платформа в цепочке обязательна: без неё видно только соотношение причин суммарно,
                // а вопрос стоит иначе — какая ступень проверки работает на какой платформе. На iOS
                // `matchAll` не отдаёт открытое PWA вовсе, там единственный рабочий признак —
                // `presence-beacon`; на десктопе ожидаются `focused-client` и `visible-client`.
                //
                // Добавляем только когда причина есть — иначе разбивка плодила бы серии с пустым
                // значением на каждом показанном пуше. Рост ограничен: 5 причин × 3 платформы.
                ...(reason ? [['action', 'reason', 'platform']] : []),
            ],
        }
    );

    if (hasRevision) {
        swXray.send([FEATURE, 'version', swRevision]);
    }
};

/** Счётчики запроса тела secure-пуша: `req` → `resp` либо `req` → `err`. */
export const sendApiRadar = (stage: 'req' | 'resp' | 'err', error?: unknown): void => {
    swXray.send(
        [FEATURE, 'api', stage],
        null,
        // rlog со стороны RADAR склеивался с именем проекта — формат сохраняем, чтобы записи
        // не разъехались с уже накопленными.
        error === undefined ? undefined : { rlog: 'cloud_WebPushSw', rlog_message: { message: String(error) } }
    );
};

/** Счётчик раската: одна отметка на устройство на ревизию (`activate` срабатывает единожды для
 * версии воркера). Отвечает на вопрос «как быстро расходится новая ревизия», в отличие от
 * `version`, который взвешен трафиком пушей.
 */
export const sendActivatedRadar = (): void => {
    if (hasRevision) {
        swXray.send([FEATURE, 'activated', swRevision]);
    }
};

/**
 * Дождаться отправленных радаров.
 *
 * Обязателен в конце каждого хендлера, включая ранние выходы: запрос уходит сразу, но дожить до
 * его завершения воркеру даёт только это ожидание внутри `event.waitUntil`.
 */
export const flushPushAnalytics = (): Promise<void> => swXray.flush();
